// Waitlist — email input + role selector (3 cards) + role multi-select chips
// (6 operators) + optional textarea + copper submit. On submit: success card.
// Source canonical: design-system/project/homepage/index.html § Waitlist.

import { useState, type FormEvent } from 'react';
import Portrait from '../components/Portrait';
import { ROSTER } from '../data/roster';

type Role = 'msme' | 'enterprise' | 'dev';

const ROLE_OPTIONS: { k: Role; l: string; h: string }[] = [
  { k: 'msme',       l: 'MSME / D2C brand', h: 'Hire your first role' },
  { k: 'enterprise', l: 'Enterprise',       h: 'Hire roles at scale' },
  { k: 'dev',        l: 'Developer',        h: 'Build on the platform' },
];

export default function Waitlist() {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<Role>('msme');
  const [interest, setInterest] = useState<Set<string>>(new Set(['priya']));
  const [submitted, setSubmitted] = useState(false);
  const [queued] = useState(() => Math.floor(Math.random() * 400) + 600);

  function toggle(id: string) {
    const n = new Set(interest);
    if (n.has(id)) n.delete(id); else n.add(id);
    setInterest(n);
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setSubmitted(true);
  }

  return (
    <section
      id="waitlist"
      style={{
        padding: '96px 40px',
        background: 'linear-gradient(180deg, #1A1918 0%, #221814 100%)',
        borderTop: '1px solid rgba(255,255,255,0.04)',
        position: 'relative', overflow: 'hidden',
      }}
    >
      {/* Top hairline copper accent */}
      <div style={{
        position: 'absolute', top: 0, left: '20%', right: '20%', height: 1,
        background: 'linear-gradient(90deg, transparent 0%, rgba(212,131,94,0.50) 50%, transparent 100%)',
      }}/>
      {/* Copper glow bottom-right */}
      <div style={{
        position: 'absolute', inset: 0, pointerEvents: 'none',
        background: 'radial-gradient(ellipse at bottom right, rgba(212,131,94,0.20) 0%, rgba(212,131,94,0.06) 35%, transparent 70%)',
      }}/>
      {/* Steel glow top-left */}
      <div style={{
        position: 'absolute', top: '10%', left: -200, width: 600, height: 600, pointerEvents: 'none',
        background: 'radial-gradient(circle, rgba(94,131,179,0.08) 0%, transparent 70%)',
      }}/>

      {/* Header (centered) */}
      <div style={{ maxWidth: 760, margin: '0 auto', textAlign: 'center', position: 'relative' }}>
        <span style={{
          fontFamily: 'IBM Plex Mono, monospace',
          fontSize: 11, fontWeight: 600,
          letterSpacing: '0.12em', textTransform: 'uppercase',
          color: '#D4835E',
        }}>Early adopters</span>
        <h2 style={{
          fontFamily: 'Fraunces, serif',
          fontSize: 52, fontWeight: 400,
          fontVariationSettings: "'opsz' 96",
          letterSpacing: '-0.02em', lineHeight: 1.05,
          color: '#F5F4F2',
          margin: '10px 0 14px',
        }}>
          Tell us what you'd hire.
        </h2>
        <p style={{
          fontFamily: 'Inter Tight, system-ui, sans-serif',
          fontSize: 16, lineHeight: 1.6,
          color: '#C7C3BD',
          maxWidth: 560, margin: '0 auto 36px',
        }}>
          Onboarding founding customers by hand. Pick what you'd hire — we'll match you, or build the role that's missing.
        </p>
      </div>

      {/* Form / Success state */}
      <div style={{ maxWidth: 680, margin: '0 auto', position: 'relative' }}>
        {submitted ? (
          <div style={{
            background: '#232120',
            border: '1px solid rgba(74,222,128,0.40)',
            borderRadius: 14,
            padding: 40,
            textAlign: 'center',
            boxShadow: '0 24px 60px -24px rgba(0,0,0,0.6), 0 0 32px -8px rgba(74,222,128,0.20)',
          }}>
            <div style={{
              width: 56, height: 56, borderRadius: 9999,
              background: 'rgba(74,222,128,0.16)',
              border: '1px solid rgba(74,222,128,0.40)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 18px',
            }}>
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                <path d="m5 12 5 5L20 7" stroke="#4ADE80" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <div style={{ fontFamily: 'Fraunces, serif', fontSize: 28, fontWeight: 500, color: '#F5F4F2', marginBottom: 8 }}>You're on the list.</div>
            <div style={{ fontSize: 14, color: '#C7C3BD', marginBottom: 18 }}>We'll reach out within 48 hours. Bangalore / Mumbai — expect a coffee invite.</div>
            <div style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 11, color: '#908A82' }}>
              Queued as <b style={{ color: '#D4835E' }}>#{queued}</b> · Batch 03
            </div>
          </div>
        ) : (
          <form
            onSubmit={handleSubmit}
            style={{
              background: '#232120',
              border: '1px solid rgba(255,255,255,0.06)',
              borderRadius: 14,
              padding: 32,
              boxShadow: '0 24px 60px -24px rgba(0,0,0,0.6)',
              display: 'flex', flexDirection: 'column', gap: 22,
            }}
          >
            {/* Work email */}
            <div>
              <label style={{ display: 'block', fontFamily: 'IBM Plex Mono, monospace', fontSize: 10, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#908A82', marginBottom: 8 }}>
                Work email
              </label>
              <input
                type="email" required
                value={email} onChange={e => setEmail(e.target.value)}
                placeholder="you@company.com"
                style={{
                  width: '100%', padding: '12px 14px',
                  border: '1px solid rgba(255,255,255,0.10)',
                  borderRadius: 8,
                  fontSize: 14,
                  fontFamily: 'inherit',
                  color: '#F5F4F2',
                  outline: 'none',
                  background: '#1A1918',
                }}
              />
            </div>

            {/* Role selector — 3 cards */}
            <div>
              <label style={{ display: 'block', fontFamily: 'IBM Plex Mono, monospace', fontSize: 10, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#908A82', marginBottom: 8 }}>
                I'm signing up as
              </label>
              <div className="waitlist-role-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
                {ROLE_OPTIONS.map(o => {
                  const on = role === o.k;
                  return (
                    <button
                      key={o.k}
                      type="button"
                      onClick={() => setRole(o.k)}
                      style={{
                        padding: 14, borderRadius: 10,
                        border: `1px solid ${on ? 'rgba(212,131,94,0.50)' : 'rgba(255,255,255,0.08)'}`,
                        background: on ? 'rgba(212,131,94,0.10)' : '#1A1918',
                        textAlign: 'left',
                        cursor: 'pointer',
                        fontFamily: 'inherit',
                        transition: 'all 150ms ease',
                      }}
                    >
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#F5F4F2' }}>{o.l}</div>
                      <div style={{ fontSize: 11, color: '#908A82', marginTop: 3 }}>{o.h}</div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Multi-select role chips — 6 operators */}
            <div>
              <label style={{ display: 'block', fontFamily: 'IBM Plex Mono, monospace', fontSize: 10, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#908A82', marginBottom: 8 }}>
                Which roles are you curious about? <span style={{ color: '#5A554F', textTransform: 'none', letterSpacing: 0 }}>(multi)</span>
              </label>
              <div className="waitlist-role-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
                {ROSTER.map(a => {
                  const on = interest.has(a.id);
                  return (
                    <button
                      key={a.id}
                      type="button"
                      onClick={() => toggle(a.id)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 10,
                        padding: '10px 12px',
                        borderRadius: 8,
                        cursor: 'pointer',
                        textAlign: 'left',
                        border: `1px solid ${on ? 'rgba(212,131,94,0.50)' : 'rgba(255,255,255,0.08)'}`,
                        background: on ? 'rgba(212,131,94,0.10)' : '#1A1918',
                        fontFamily: 'inherit',
                        transition: 'all 150ms ease',
                      }}
                    >
                      <Portrait src={a.photo} name={a.name} size={28}/>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: '#F5F4F2' }}>{a.name.split(' ')[0]}</div>
                        <div style={{ fontSize: 10, color: '#908A82', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{a.role}</div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Optional textarea */}
            <div>
              <label style={{ display: 'block', fontFamily: 'IBM Plex Mono, monospace', fontSize: 10, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#908A82', marginBottom: 8 }}>
                What role would you hire first? <span style={{ color: '#5A554F', textTransform: 'none', letterSpacing: 0 }}>(optional)</span>
              </label>
              <textarea
                rows={3}
                placeholder="e.g. a junior accountant for monthly books, or a CX agent that handles our Instagram DMs"
                style={{
                  width: '100%', padding: '12px 14px',
                  border: '1px solid rgba(255,255,255,0.10)',
                  borderRadius: 8,
                  fontSize: 13.5,
                  fontFamily: 'inherit',
                  color: '#F5F4F2',
                  outline: 'none',
                  background: '#1A1918',
                  resize: 'vertical',
                  lineHeight: 1.5,
                }}
              />
            </div>

            {/* Submit */}
            <button
              type="submit"
              style={{
                padding: '14px 20px',
                background: '#D4835E',
                color: '#1A1918',
                border: 'none', borderRadius: 8,
                fontSize: 15, fontWeight: 700,
                fontFamily: 'inherit', cursor: 'pointer',
              }}
            >
              Request early access →
            </button>

            <div style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 10, color: '#908A82', textAlign: 'center' }}>
              No spam. We read every response. Reply rate &lt; 48h.
            </div>
          </form>
        )}
      </div>
    </section>
  );
}
